import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  setDoc,
  onSnapshot, 
  query, 
  orderBy, 
  serverTimestamp,
  Timestamp,
  where,
  getDocs
} from 'firebase/firestore';
import { db, auth } from '../config/firebase.config';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'rider' | 'customer';
  senderId: string;
  senderName: string;
  timestamp: Date;
  status?: 'sent' | 'delivered' | 'read';
  isRead: boolean;
}

export class ChatService {
  /**
   * Send a message in a job/order chat
   * @param jobId - The job/order ID
   * @param message - Message text
   * @param senderName - Name of the sender (rider name)
   */
  async sendMessage(jobId: string, message: string, senderName: string): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to send messages');
      }

      const messagesRef = collection(db, 'chats', jobId, 'messages');
      
      const messageData = {
        text: message,
        message: message,
        sender: 'rider',
        senderType: 'rider',
        senderId: currentUser.uid,
        senderName: senderName,
        timestamp: serverTimestamp(),
        status: 'sent',
        isRead: false,
        createdAt: Timestamp.now()
      };

      await addDoc(messagesRef, messageData);

      const chatRef = doc(db, 'chats', jobId);
      await setDoc(
        chatRef,
        {
          jobId,
          lastMessage: message,
          lastMessageAt: serverTimestamp(),
          lastSender: 'rider',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      console.log('✅ Message sent to chat:', jobId);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time messages for a job
   * @param jobId - The job/order ID
   * @param callback - Callback function to receive messages
   * @returns Unsubscribe function
   */
  subscribeToMessages(
    jobId: string, 
    callback: (messages: ChatMessage[]) => void
  ): () => void {
    try {
      const messagesRef = collection(db, 'chats', jobId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const messages: ChatMessage[] = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            messages.push({
              id: doc.id,
              text: data.text || data.message || '',
              sender: data.sender || data.senderType || 'customer',
              senderId: data.senderId || '',
              senderName: data.senderName || 'User',
              timestamp: data.timestamp?.toDate() || new Date(),
              status: data.status || 'sent',
              isRead: data.isRead || false
            });
          });

          // Auto-mark customer messages as read
          this.markMessagesAsRead(jobId, messages);
          
          callback(messages);
        },
        (error) => {
          console.error('❌ Error listening to messages:', error);
          callback([]);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('❌ Error subscribing to messages:', error);
      return () => {};
    }
  }

  /**
   * Mark customer messages as read by rider
   * @param jobId - The job/order ID
   * @param messages - Array of messages
   */
  private async markMessagesAsRead(jobId: string, messages: ChatMessage[]): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      // Find unread messages from customer
      const unreadMessages = messages.filter(
        msg => msg.sender === 'customer' && !msg.isRead
      );

      // Mark each as read
      for (const msg of unreadMessages) {
        const messageRef = doc(db, 'chats', jobId, 'messages', msg.id);
        await updateDoc(messageRef, {
          isRead: true,
          status: 'read',
          readAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('❌ Error marking messages as read:', error);
    }
  }

  /**
   * Get unread message count for a job
   * @param jobId - The job/order ID
   * @returns Number of unread messages
   */
  async getUnreadCount(jobId: string): Promise<number> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return 0;

      const messagesRef = collection(db, 'chats', jobId, 'messages');
      const q = query(
        messagesRef,
        where('sender', '==', 'customer'),
        where('isRead', '==', false)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('❌ Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Initialize chat for a job (create chat document if doesn't exist)
   * @param jobId - The job/order ID
   * @param riderInfo - Rider information
   * @param customerInfo - Customer information
   */
  async initializeChat(
    jobId: string, 
    riderInfo: { id: string; name: string; phone: string },
    customerInfo: { name: string; phone: string }
  ): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', jobId);
      
      await updateDoc(chatRef, {
        jobId: jobId,
        rider: riderInfo,
        customer: customerInfo,
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }).catch(async () => {
        // If document doesn't exist, create it
        const { setDoc } = await import('firebase/firestore');
        await setDoc(chatRef, {
          jobId: jobId,
          rider: riderInfo,
          customer: customerInfo,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      console.log('✅ Chat initialized for job:', jobId);
    } catch (error) {
      console.error('❌ Error initializing chat:', error);
    }
  }
}

// Export singleton instance
export const chatService = new ChatService();
